import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, Nav, NavItem, NavLink } from "reactstrap";
import { Link, useParams } from "react-router-dom";
import cx from "classnames";
import PropTypes from "prop-types";

import global from "../../scss/dhp.scss";

import { Icon } from "../ui/icon";
import { Input } from "../ui/input";
import { getImgDimensionFromUrl, maskEmail, preventFormSubmitOnEnter } from "../../_helpers/utils";
import domtoimage from "dom-to-image";
import { acceptProposal, sendSignerVerificationOTP } from "../../store/actions/editorActions";
import { useSocket } from "../../hooks/useSocket";
import { SYNC_SIGNATURE } from "../../constants/socket";
import LoadingBar, { showLoading, hideLoading } from "react-redux-loading-bar";

let style = Object.assign({}, global);

const Esignature = props => {
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const ctxRef = useRef(null);
  const tabOptions = ["Type", "Draw", "Upload"];

  const [uploadStart, setUploadStart] = useState();
  const [uploadErrorMsg, setUploadErrorMsg] = useState();
  const [inputErrorMsg, setInputErrorMsg] = useState();
  const [isDrawing, setIsDrawing] = useState(false);

  const validateInput = input => {
    const allowedPattern = /^[A-Za-z .-]*$/;

    if (!allowedPattern.test(input)) {
      setInputErrorMsg("Only letters, spaces, periods (.), and hyphens (-) are allowed.");
    } else {
      setInputErrorMsg();
      props.setSignature(input);
    }
  };

  const drawEvent = {
    // Start drawing
    startDrawing: e => {
      const { offsetX, offsetY } = e.nativeEvent;
      ctxRef.current.beginPath();
      ctxRef.current.moveTo(offsetX, offsetY);
      ctxRef.current.lineWidth = 2;
      ctxRef.current.lineCap = "round";
      ctxRef.current.strokeStyle = "black"; // Set default color
      setIsDrawing(true);
      isDrawingRef.current = true; // immediate update
    },

    // Draw on mouse move
    draw: e => {
      if (!isDrawingRef.current) return;

      const { offsetX, offsetY } = e.nativeEvent;
      ctxRef.current.lineTo(offsetX, offsetY);
      ctxRef.current.stroke();
    },

    // Stop drawing and save canvas data
    stopDrawing: () => {
      if (!isDrawingRef.current) return; // prevent saving if not drawing

      setIsDrawing(false);
      isDrawingRef.current = false;
      ctxRef.current.closePath();

      // Save canvas image to localStorage
      const canvasData = canvasRef.current.toDataURL();
      localStorage.setItem("canvasDrawing", canvasData);
      props.setSignature(canvasData);
    },
  };

  const uploadFile = e => {
    const file = e.target.files[0];
    const fileSize = file.size / 1e6;

    // upload file type validation
    if (file.type !== "image/png" && file.type !== "image/jpeg") {
      setUploadErrorMsg("Invalid Format!!");
    }
    // upload file size validation
    else if (fileSize > 1) {
      setUploadErrorMsg("File Size should be maximum 1 MB");
    }
    // upload image
    else {
      const reader = new FileReader();

      reader.onprogress = function (event) {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          const progressBar = document.getElementById("progress-bar");
          progressBar.style.width = percent + "%";
          progressBar.innerText = percent + "%";
          document.getElementById("progress-container").style.display = "block";
          setUploadStart(true);
        }
      };

      reader.onload = function (event) {
        setImageDimansion(event.target.result);
      };

      reader.onerror = function () {
        alert("An error occurred while reading the file.");
      };

      reader.readAsDataURL(file);
    }

    setTimeout(() => {
      setUploadErrorMsg();
    }, 3000);
  };

  const setImageDimansion = async url => {
    const { width: imgWidth, height: imgHeight } = await getImgDimensionFromUrl(url);

    props.setSignature(url);
    setUploadStart(false);

    setTimeout(() => {
      const containerRatio = 300 / 60;
      const imageRatio = imgWidth / imgHeight;

      if (imageRatio > containerRatio) {
        document.getElementById("preview").style.width = "300px";
        document.getElementById("preview").style.height = "auto";
      } else {
        document.getElementById("preview").style.width = "auto";
        document.getElementById("preview").style.height = "60px";
      }
    }, 50);
  };

  const clearSignature = () => {
    props.setSignature();

    // clear input fields
    if (props.activeTab === 0) {
      setInputErrorMsg();
      document.getElementById("signature_input").value = "";
    }

    // Clears the entire canvas
    if (props.activeTab === 1) {
      ctxRef.current.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      localStorage.removeItem("canvasDrawing"); // Remove saved data
    }

    // clear upload error msg
    if (props.activeTab === 2) setUploadErrorMsg();
  };

  useEffect(() => {
    // prepare canvas
    if (props.activeTab === 1) {
      const canvas = canvasRef.current;
      canvas.width = 400;
      canvas.height = 100;
      ctxRef.current = canvas.getContext("2d");
    }
  }, [props.activeTab]);

  useEffect(() => {
    // hold canvas data if i go back to previous modal tab and came back
    if (props.activeTab === 1) {
      const canvas = canvasRef.current;
      canvas.width = 400;
      canvas.height = 100;
      ctxRef.current = canvas.getContext("2d");

      // Load saved drawing from localStorage
      const savedData = localStorage.getItem("canvasDrawing");
      if (savedData) {
        const img = new Image();
        img.src = savedData;
        img.onload = () => ctxRef.current.drawImage(img, 0, 0);
      }
    }

    // hold image width and height if i go back to previous modal tab and came back
    if (props.activeTab === 2 && props.signature) {
      setImageDimansion(document.getElementById("preview").src);
    }
  }, []);

  return (
    <>
      <Nav tabs className={cx(style["line-tabs"], style["mx-1"], style["mt-2"])}>
        {tabOptions?.map((indextabOption, index) => (
          <NavItem key={index}>
            <NavLink
              className={props.activeTab === index ? "active" : ""}
              onClick={() => {
                props.setSignature();
                setInputErrorMsg();
                setUploadErrorMsg();
                props.setActiveTab(index);
              }}>
              {indextabOption}
            </NavLink>
          </NavItem>
        ))}
      </Nav>

      {/* clear signature */}
      <span onClick={clearSignature} className={cx(style["clear"], style["custom-tooltip"])}>
        <Icon icon="ui-reset" />
        <div className={cx(style["custom-tooltip-content"], style["bottom"])}>Clear</div>
      </span>

      {/* For type the signature */}
      {props.activeTab === 0 && (
        <div className={style["text-center"]}>
          <Input
            id="signature_input"
            className="signature-input"
            cssModule={style}
            returnType="formGroup"
            type="text"
            label=""
            value={props.signature}
            placeholder="Type your signature"
            maxlength={50}
            onChange={e => {
              e.preventDefault();
              validateInput(e.target.value);
            }}
            showerror={inputErrorMsg}
            autoFocus
            required
          />
        </div>
      )}

      {/* For Draw the signature */}
      {props.activeTab === 1 && (
        <canvas
          ref={canvasRef}
          id="signature-canvas"
          onMouseDown={drawEvent.startDrawing}
          onMouseMove={drawEvent.draw}
          onMouseUp={drawEvent.stopDrawing}
          onMouseLeave={drawEvent.stopDrawing}></canvas>
      )}

      {/* For upload signature */}
      {props.activeTab === 2 && (
        <div className={style["upload-button-area"]}>
          {/* upload button section */}
          {!props.signature && (
            <>
              <div id="upload-button">
                <div className={cx(style["fileUpload"], style["w-100"])}>
                  <input
                    type="file"
                    id="fileInput"
                    accept="image/jpeg, image/png"
                    onChange={e => {
                      setUploadErrorMsg(), uploadFile(e);
                    }}
                  />
                  <span className={cx(style["btn"], style["btn-border"])}>
                    <Icon icon="ui-upload" /> Upload
                  </span>
                </div>
                <span className={style["info"]}>JPG, PNG (Max 1 MB)</span>
              </div>

              {/* error message section */}
              {uploadErrorMsg && (
                <div id="error-msg" className={cx(style["alert"], style["alert-danger"])}>
                  {uploadErrorMsg}
                </div>
              )}

              {/* progress bar */}
              <div
                id="progress-container"
                style={{
                  width: "100%",
                  background: "#f3f3f3",
                  display: "none",
                }}>
                <div
                  id="progress-bar"
                  style={{
                    width: " 0%",
                    height: "20px",
                    background: "green",
                    color: "white",
                  }}>
                  0%
                </div>
              </div>
            </>
          )}

          {/* show uploaded image section */}
          {props.signature && <img id="preview" className={style["mt-3"]} src={props.signature} />}
        </div>
      )}
    </>
  );
};
//Props type validation
Esignature.propTypes = {
  signature: PropTypes.string,
  setSignature: PropTypes.func,
  activeTab: PropTypes.number,
  setActiveTab: PropTypes.func,
};

const AcceptSignatureFormModal = props => {
  const date_format = useSelector(state => state?.auth?.user?.date_format) ?? "MM-DD-YYYY";

  const datePickerRef = useRef(null);

  const dateFormat = date_format.replaceAll("-", "/");
  const initialValues = props.previousformValue
    ? props.previousformValue
    : props?.formFields?.fields
    ? Object.fromEntries(
        Object.entries(props.formFields.fields).map(([key, field]) =>
          field.fieldName === "Name"
            ? [field.fieldName, props.signerName]
            : field.fieldName === "Email"
            ? [field.fieldName, props.signerEmail]
            : [field.fieldName, ""]
        )
      )
    : { Name: props.signerName, Email: props.signerEmail }; // Initial state for form values

  // State for form values and errors
  const [formValues, setFormValues] = useState(initialValues);
  const [dropdownFieldOptions, setDropdownFieldOptions] = useState();
  const [isValidForm, setIsValidForm] = useState(false);

  // Handle input change
  const handleChange = e => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  // Handle form submission
  const handleSubmit = e => {
    e.preventDefault();
    props.setPreviousformValue(formValues);
    props.setModalTab(2);
  };

  // create dynamic object for dropdown filed options
  useEffect(() => {
    if (props?.formFields?.fields) {
      const updatedDropdowns = {}; // Temporary object to store values

      Object.entries(props?.formFields?.fields).map(([key, field], index) => {
        if (field.fieldType === "dropdown") {
          const arr = field.options.split(",").map(item => item.trim());
          const keyValueArray = arr.map(value => ({ name: value }));
          updatedDropdowns[key] = keyValueArray; // Store in temp object
        }
      });
      setDropdownFieldOptions(updatedDropdowns);
    }
  }, []);

  // check validation on input field value change
  useEffect(() => {
    if (props?.formFields?.fields) {
      let validationErrors = {};

      // Validate mandatory fields
      Object.entries(props.formFields.fields).forEach(([key, field]) => {
        if (field.mandatory && !formValues[field.fieldName]?.trim()) {
          validationErrors[field.fieldName] = field.fieldName;
        }
      });

      // If errors exist, update state; otherwise, submit form
      if (Object.keys(validationErrors).length > 0) {
        setIsValidForm(false);
      } else {
        setIsValidForm(true);
      }
    } else setIsValidForm(true);
  }, [formValues]);

  return (
    <Form id="acceptSignatureForm" onKeyDown={e => preventFormSubmitOnEnter(e)}>
      <div className={cx(style["customScroll"], style["scroll-Y"], style["accept-form"], style["mt-3"])}>
        {/* signer name */}
        <Input label="Name" type="text" name="nameTextField" value={props.signerName} required={true} readOnly={true} />

        {/* signer email */}
        <Input
          label="Email"
          type="text"
          name="emailTextField"
          value={maskEmail(props.signerEmail)}
          required={true}
          readOnly={true}
        />

        {/* Dynamic fields */}
        {props?.formFields?.fields &&
          Object.entries(props.formFields.fields).map(
            ([key, field], index) =>
              index !== 0 &&
              index !== 1 && (
                <>
                  {/* for dynamic text field */}
                  {field.fieldType === "text" && (
                    <Input
                      label={field.fieldName}
                      type="text"
                      name={field.fieldName}
                      required={field.mandatory}
                      value={formValues[field.fieldName]}
                      onChange={e => {
                        handleChange(e);
                      }}
                    />
                  )}

                  {/* for dynamic date field */}
                  {field.fieldType === "date" && (
                    <div className={style["custom-date-picker"]}>
                      <Input
                        label={field.fieldName}
                        type="date_calendar"
                        name={field.fieldName}
                        dateFormat={dateFormat}
                        required={field.mandatory}
                        updateState={e => {
                          if (e) setFormValues({ ...formValues, [field.fieldName]: e });
                        }}
                        defaultValue={formValues[field.fieldName]}
                      />
                    </div>
                  )}

                  {/* for dynamic dropdown field */}
                  {field.fieldType === "dropdown" && (
                    <Input
                      cssModule={style}
                      returnType="formGroup"
                      type="dropdown"
                      label={field.fieldName}
                      required={field.mandatory}
                      classNamePrefix="select"
                      getOptionValue={option => option.name}
                      getOptionLabel={option => <span>{option.name}</span>}
                      options={dropdownFieldOptions?.[key] ?? []}
                      updateState={option => {
                        setFormValues({ ...formValues, [field.fieldName]: option.name });
                      }}
                      defaultValue={{ name: formValues[field.fieldName] }}
                    />
                  )}

                  {/* for dynamic number field */}
                  {field.fieldType === "number" && (
                    <Input
                      className={style["form-control"]}
                      label={field.fieldName}
                      type="text"
                      name={field.fieldName}
                      required={field.mandatory}
                      value={formValues[field.fieldName]}
                      onKeyPress={e => {
                        if (e.key.length === 1 && !/[0-9.]/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onChange={e => {
                        handleChange(e);
                      }}
                    />
                  )}
                </>
              )
          )}
      </div>
      <Button type="button" className={style["w-100"]} color="primary" disabled={!isValidForm} onClick={handleSubmit}>
        Proceed to E-Signature
      </Button>
    </Form>
  );
};
//Props type validation
AcceptSignatureFormModal.propTypes = {
  formFields: PropTypes.obj,
  signerName: PropTypes.string,
  signerEmail: PropTypes.string,
  setModalTab: PropTypes.func,
  previousformValue: PropTypes.obj,
  setPreviousformValue: PropTypes.func,
};

const PublishSignatureModal = props => {
  const dispatch = useDispatch();
  const webSocket = useSocket();
  const { companyId } = useParams();

  const [modalTab, setModalTab] = useState(0);
  const [activeSignerIndex, setActiveSignerIndex] = useState();
  const [previousformValue, setPreviousformValue] = useState();
  const [activeTab, setActiveTab] = useState(0);
  const [signature, setSignature] = useState();
  const [isAgtreeCheckboxTicked, setIsAgtreeCheckboxTicked] = useState();
  const [verificationCode, setVerificationCode] = useState("");
  const [isVisibleOtp, setIsVisibleOtp] = useState(false);
  const [showError, setShowError] = useState();
  const [isFinalAcceptButtonDisable, setIsFinalAcceptButtonDisable] = useState(true);
  const [resendMsg, setResendMsg] = useState(false);
  const [addButtonLoaded, setAddButtonLoaded] = useState();

  const closeModal = () => {
    props.setShowModal(!props.showModal);
  };

  const handleSignerClick = (e, index) => {
    if (!props.acceptedSignerListId.includes(props.signerList[index]._id)) {
      setModalTab(1);
      setActiveSignerIndex(index);
    } else e.preventDefault();
  };

  const sendOTP = clicked => {
    let payload = {
      company_id: companyId,
      proposal_id: props.documentId,
      email: props.signerList[activeSignerIndex].email,
    };

    dispatch(sendSignerVerificationOTP(payload));
    if (clicked) {
      setResendMsg(true);
      setTimeout(() => setResendMsg(false), 3000);
    }
  };

  const handleVarificationCodeChange = e => {
    setShowError("");
    if (e?.target?.value.trim().length > 6) return;
    setVerificationCode(e?.target?.value.trim());
  };

  const toggleOtpVisiblity = () => {
    setIsVisibleOtp(!isVisibleOtp);
  };

  const acceptDocument = () => {
    setAddButtonLoaded(true);
    setIsFinalAcceptButtonDisable(true);
    if (activeTab === 0) convertHtmlToImage(); // if signature input field covert taht html to png image
    // if signature type is draw then cropped the canvas content area only for upload
    else if (activeTab === 1) {
      const cropped = cropCanvas(document.getElementById("signature-canvas"));
      document.body.appendChild(cropped); // Append to body or convert to image
      const canvasCroppedData = cropped.toDataURL(); // Get base64 image
      submitSignature(canvasCroppedData);
    } else submitSignature();
  };

  const convertHtmlToImage = () => {
    let node = document.createElement("div");
    node.classList.add("signature-image");
    node.innerText = signature;

    // Append to the DOM (hidden to prevent UI issues)
    document.body.appendChild(node);

    domtoimage
      .toPng(node, { useCORS: true })
      .then(dataUrl => {
        submitSignature(dataUrl);
      })
      .catch(error => console.error("Image generation failed", error));
  };

  const cropCanvas = canvas => {
    const ctx = canvas.getContext("2d");
    const width = canvas.width;
    const height = canvas.height;
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;

    let top = null,
      left = null,
      right = null,
      bottom = null;

    // Find bounds of non-transparent pixels
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = (y * width + x) * 4;
        const alpha = data[idx + 3];

        if (alpha !== 0) {
          if (top === null) top = y;
          if (left === null || x < left) left = x;
          if (right === null || x > right) right = x;
          bottom = y;
        }
      }
    }

    if (top === null) {
      // No content found
      return null;
    }

    const croppedWidth = right - left + 1;
    const croppedHeight = bottom - top + 1;

    // Create new canvas and draw the cropped content
    const croppedCanvas = document.createElement("canvas");
    croppedCanvas.width = croppedWidth;
    croppedCanvas.height = croppedHeight;
    const croppedCtx = croppedCanvas.getContext("2d");
    croppedCtx.putImageData(ctx.getImageData(left, top, croppedWidth, croppedHeight), 0, 0);

    return croppedCanvas;
  };

  const submitSignature = type_signature => {
    const data = new FormData();
    data.append("email", props.signerList[activeSignerIndex].email);
    data.append("verification_code", verificationCode);
    data.append("signature_method", activeTab === 0 ? "type" : activeTab === 1 ? "draw" : "upload");
    data.append("signer_details", JSON.stringify(previousformValue));
    data.append("signature_file", type_signature ? type_signature : signature);
    if(props?.widgetDelta) data.append("widget_delta", props.widgetDelta)
    dispatch(showLoading());
    dispatch(acceptProposal(data, companyId, props.documentId))
      .then(r => {
        if (r.data.success) {
          // add signer id on accepeted signer list array
          let accepetdId = props.acceptedSignerListId;
          accepetdId.push(props.signerList[activeSignerIndex]._id);
          props.setAcceptedSignerListId(accepetdId);

          // check after signed doc is fully or partialy sigbed
          if (r.data.partially_signed) props.setIsFullyAccepeted(false);
          else props.setIsFullyAccepeted(true);

          // Brodcast
          const subscribe = {
            type: SYNC_SIGNATURE,
            companyId,
            documentId: props.documentId,
            signerId: props.signerList[activeSignerIndex]._id,
          };
          webSocket.send(JSON.stringify(subscribe));

          if (props?.enabledEditing) {
            webSocket.send(
              JSON.stringify({
                type: "dh:public:page:collaboration",
                companyId,
                documentId: props.documentId,
                widget_delta: props.widgetDelta,
              })
            );
          }

          dispatch(hideLoading());
          setModalTab(3);
          setIsFinalAcceptButtonDisable(false);
          setAddButtonLoaded(false);
        }
      })
      .catch(e => {
        setShowError("Invalid verification code");
      });
  };

  useEffect(() => {
    if (modalTab === 2) {
      sendOTP();
      setVerificationCode("");
      setIsAgtreeCheckboxTicked(false);
    }
  }, [modalTab]);

  useEffect(() => {
    // check validation for final accept button enable
    if (signature && isAgtreeCheckboxTicked && verificationCode.length === 6) setIsFinalAcceptButtonDisable(false);
    else setIsFinalAcceptButtonDisable(true);
  }, [signature, isAgtreeCheckboxTicked, verificationCode]);

  return (
    <div>
      <span className={cx(style["cross-modal"], style["rounded"])} onClick={closeModal}>
        <Icon icon="ui-close" />
      </span>

      {/* modal 1st screen */}
      {modalTab === 0 && (
        <>
          <div
            className={cx(
              style["text-center"],
              style["d-flex"],
              style["flex-column"],
              style["align-items-center"],
              style["mb-4"],
              style["mt-n2"]
            )}>
            <div className={cx(style["greenBox-icon"], style["mb-3"])}>
              <Icon icon="ui-signature"></Icon>
            </div>
            <h4 className={style["fw-7"]}>Accept the Document</h4>
            <h6 className={style["fw-6"]}>To accept, please sign the document</h6>
          </div>

          {/* show signer list */}
          <div className={cx(style["customScroll"], style["scroll-Y"], style["signer-list-wrap"])}>
            <ul className={style["signer-list"]}>
              {props.signerList.map((signer, index) => (
                <li
                  key={index}
                  onClick={e => handleSignerClick(e, index)}
                  className={cx({
                    [style["signature-accepeted"]]: props.acceptedSignerListId.includes(signer._id),
                  })}>
                  <div className={cx(style["user-info"], style["d-flex"], style["align-items-center"])}>
                    <div className={style["me-1"]}>
                      <div className={cx(style["user-icon"], style[`signature-${index}`])}>
                        <Icon icon="ui-user"></Icon>
                      </div>
                    </div>

                    <div className={cx(style["ms-2"], style["user-details"], style["w-75"])}>
                      <h6 className={cx(style["text-truncate"], style["mb-0"])}>{signer.name}</h6>
                      <div className={cx(style["text-muted"], style["text-truncate"], style["font-13"])}>
                        {maskEmail(signer.email)}
                      </div>
                    </div>

                    {!props.acceptedSignerListId.includes(signer._id) && (
                      <div className={cx(style["ms-auto"], style["arrow-right-animation"], style["d-flex"])}>
                        <Icon icon="ui-arrow-right" />
                      </div>
                    )}

                    {props.acceptedSignerListId.includes(signer._id) && (
                      <span className={cx(style["btn"], style["btn-border"], style["greenBox-icon"])}>
                        <Icon icon="tick-success" /> Accepted
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}

      {/* modal 2nd screen */}
      {modalTab === 1 && (
        <>
          <div className={cx(style["d-flex"], style["align-items-center"])}>
            <span
              className={cx(style["me-2"], style["cursor-pointer"])}
              onClick={() => {
                setModalTab(0);
                setPreviousformValue();
                setSignature();
                setActiveTab(0);
              }}>
              <Icon icon="ui-arrow-left" />
            </span>
            <h4 className={cx(style["fw-7"], style["mb-1"])}>Accept the Document</h4>
          </div>

          {/* accept signature form with dynamic fields */}
          <AcceptSignatureFormModal
            formFields={props.signerList[activeSignerIndex]?.signatureForm}
            signerName={props.signerList[activeSignerIndex].name}
            signerEmail={props.signerList[activeSignerIndex].email}
            setModalTab={setModalTab}
            previousformValue={previousformValue}
            setPreviousformValue={setPreviousformValue}
          />
        </>
      )}

      {/* modal 3rd screen */}
      {modalTab === 2 && (
        <>
          <div className={cx(style["d-flex"], style["align-items-center"])}>
            <span className={cx(style["me-2"], style["cursor-pointer"])} onClick={() => setModalTab(1)}>
              <Icon icon="ui-arrow-left" />
            </span>
            <h4 className={cx(style["fw-7"], style["mb-1"])}>Accept the Document</h4>
          </div>

          <div className={style["mt-3"]}>
            {/* E- signature area */}
            <h6 className={style["fw-6"]}>E-Signature</h6>
            <div className={cx(style["signature-tab-area"])}>
              <Esignature
                signature={signature}
                setSignature={setSignature}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
              />
            </div>

            {/* agree check box */}
            <div className={cx(style["custom-control"], style["custom-checkbox"], style["form-check"], style["mt-2"], style["mb-3"])}>
              <input
                type="checkbox"
                value=""
                className={cx(style["form-check-input"])}
                id="customCheck1"
                onChange={e => setIsAgtreeCheckboxTicked(!isAgtreeCheckboxTicked)}
              />
              <label className={cx(style["form-check-label"])} htmlFor="customCheck1">
                I agree that my e-signature is as legally binding as a handwritten one
              </label>
            </div>

            {/* verification code */}
            <Input
              label="Verification Code"
              cssModule={style}
              type={isVisibleOtp ? "text_password" : "password"}
              name="enter_verification_code"
              value={verificationCode}
              onChange={e => handleVarificationCodeChange(e)}
              toggleHandler={toggleOtpVisiblity}
              autoComplete="new-password"
              showerror={showError}
              required={true}
            />

            <p className={style["mb-4"]}>
              A verification code is sent to {props.signerList[activeSignerIndex].email} to verify your identity. Didn’t
              get it? &nbsp;
              {resendMsg && (
                <Link to="#" className={cx(style["ms-2"], style["link-green"])}>
                  Sent
                </Link>
              )}
              {!resendMsg && (
                <Link to="#" onClick={() => sendOTP(true)}>
                  Resend
                </Link>
              )}
            </p>

            <Button
              type="button"
              color="primary"
              className={cx(style["btn-block"], style["btn-loader"], {
                [style["btn-loaded"]]: addButtonLoaded,
              })}
              disabled={isFinalAcceptButtonDisable}
              onClick={acceptDocument}>
              {isFinalAcceptButtonDisable && <LoadingBar className={style["btn-loader-bg"]} />}
              Verify Code and Accept Document
            </Button>
          </div>
        </>
      )}

      {/* modal 4th screen */}
      {modalTab === 3 && (
        <div
          className={cx(
            style["text-center"],
            style["d-flex"],
            style["flex-column"],
            style["align-items-center"],
            style["mt-n2"]
          )}>
          <div className={cx(style["greenBox-icon"], style["mb-3"])}>
            <Icon icon="thumb-fill"></Icon>
          </div>
          <h4 className={cx(style["fw-7"], style["mb-4"])}>Accepted</h4>
          {props.isFullyAccepeted && (
            <>
              <p className={style["font-15"]}>
                Congratulations! You&apos;ve accepted the <br></br>
                document. You can download the PDF version <br></br>
                of the signed document from&nbsp;
                <Link
                  to="#"
                  onClick={() => {
                    props.handleDownload;
                    setTimeout(() => {
                      closeModal();
                    }, 100);
                  }}>
                  here
                </Link>
                .
              </p>
            </>
          )}
          {!props.isFullyAccepeted && (
            <>
              <p className={style["font-15"]}>
                Congratulations! You&apos;ve accepted the <br></br> document. Once all parties have accepted, <br></br>
                you’ll be notified via email.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};
//Props type validation
PublishSignatureModal.propTypes = {
  signerList: PropTypes.obj,
  showModal: PropTypes.bool,
  setShowModal: PropTypes.func,
  documentId: PropTypes.string,
  isFullyAccepeted: PropTypes.bool,
  setIsFullyAccepeted: PropTypes.func,
  acceptedSignerListId: PropTypes.obj,
  setAcceptedSignerListId: PropTypes.func,
};

export default PublishSignatureModal;
